import { Component, OnInit, ElementRef, NgZone, ChangeDetectorRef } from "@angular/core";
import { FormGroup, FormBuilder, FormControl, Validators, FormArray } from "@angular/forms";
import { ActionName } from "@shared/enums/action-name";
import { Constants } from "@shared/models/constants";
import { DataSharingLTE } from "@fs/services/data-sharing-lte";
import { ExportColumnHeaders } from "@shared/models/export-headers.model";
import { ExportService } from "@shared/services/export";
import { MapExportData } from "src/app/shared/data/MapExportData";
import { PrimaryTechnicianPipe } from "@fs/pipes/primary-technician";
import { TabOpenService } from "@services/tab-open.service";
import { Tabs } from "@enums/tab.enum";
import { TimeZoneConverter } from "@shared/pipes/timezone-converter";
import { WorkOrderService } from "@fs/services/work-order";
import { NotificationsService } from "@shared/services/notification";
import { ExportType, WOSChangeEventDestination } from "@fs/enums/common";
import { CommonMethods } from "@services/common.method";
import { StatesService } from "@shared/services/states";
import { SpinnerService } from "src/app/shared/services/spinner.service";
import { Grid } from "@fs/enums/grid.enum";
import { catchError, debounceTime, switchMap, takeUntil, tap } from "rxjs/operators";
import { of, Subject } from "rxjs";
import { GridFilter } from '@fs/services/grid.mixin';
import { customJS } from "@fs/services/customjs";

@Component({
  selector: "app-work-order-list",
  templateUrl: "./wo-list.component.html",
  styleUrls: ["./wo-list.component.scss"],
  providers: [TimeZoneConverter, PrimaryTechnicianPipe]
})
export class WorkOrderListComponent extends GridFilter implements OnInit {
  showConfirmBox: boolean = false;
  protected readonly ngUnsubscribe = new Subject();
  private dispatchingItem: any;
  private States:any[];
  private SubStates:any[];

  constructor(private $elementRef: ElementRef, private commonMethod: CommonMethods, private dataSharing: DataSharingLTE, 
    private exportServ: ExportService, private httpService: WorkOrderService, private changeDetector: ChangeDetectorRef,
    private primaryTechnician: PrimaryTechnicianPipe, private tabopen: TabOpenService, private toastr: NotificationsService, 
    private zone: NgZone, private statesService: StatesService, spinnerServ: SpinnerService) {
      super(Grid.WorkOrder, spinnerServ);
      //ToDo: Optimizing further requires significant changes in the exisitng services
      this.makeRequest$.pipe(
        debounceTime(Constants.debounceInterval),
        tap(()=> {
          this.setDefaultFilter();
        }),
        switchMap((val:string) => 
        this.httpService.GetWorkOrderList(this.isExport? this.getExportState() : this.state, val).pipe(
        catchError(() => {
          return of({ Data: [], Count: 0 });
        })
      )), 
      takeUntil(this.ngUnsubscribe))
      .subscribe(r => {
        this.spinnerServ.hideLoader(this.gridVc, this.tabCounter);
        if (!this.isExport) { 
          this.mapGridData(r);
          this.data.data.forEach(x => {
            x.checkEditDisable = this.commonMethod.IsWorkOrderEditDisabled(x);
            x.showDispatchOrderOptions = this.showDispatchOrderOptions(x);
            x.StartTime = CommonMethods.Convert24HrsTo12Hrs(x.StartTime, false);
            x.CustomerObj = { JobSite: x.JobSite, Customer: { ID: x.CustomerID, DisplayName: "" } }
          });
        } else { 
          this.mapExportData(r); 
        }
      });
      this.registerEvents();
  }

  ngOnInit() {
    this.setPageMetaFilter();
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      customJS.setScroll(this.$elementRef.nativeElement);
    });
  }
  
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  newWorkOrder() {
    this.tabopen.OpenTab({
      tabId: Tabs.FsLiteWorkOrderCreate,
      params: {},
      tabTitle: "New Work Order"
    });
  }

  viewWorkOrder(dataItem) {
    this.tabopen.OpenTab({
      tabId: Tabs.FsLiteWorkOrderView,
      params: {
        ID: dataItem.ID,
        jobsiteId: dataItem.JobSite.ID,
        customerId: dataItem.CustomerID
      },
      tabTitle: dataItem.WorkTicketNo == null ? " " : dataItem.WorkTicketNo,
      replaceIfOpen: {
        replaceTab: true,
        tabToReplace: Tabs.FsLiteWorkOrderEdit,
        action: "focus"
      }
    });
  }

  editWorkOrder(dataItem) {
    this.tabopen.OpenTab({
      tabId: Tabs.FsLiteWorkOrderEdit,
      params: {
        ID: dataItem.ID,
        jobsiteId: dataItem.JobSite.ID,
        customerId: dataItem.CustomerID
      },
      tabTitle: dataItem.WorkTicketNo == null ? " " : dataItem.WorkTicketNo,
      replaceIfOpen: {
        replaceTab: true,
        tabToReplace: Tabs.FsLiteWorkOrderView,
        action: "replace"
      }
    });
  }

  dispatchHandler(dataItem) {
    this.showConfirmBox = true;
    this.dispatchingItem = dataItem;
  }

  confirmAction(req) {
    if (req) {
      this.updateWorkOrderStatus(this.dispatchingItem);
    } else {
      this.dispatchingItem = null;
    }
    this.showConfirmBox = false;
  }

  private registerEvents() {
    this.dataSharing.OrderStatusChangeEvent.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          if (!CommonMethods.CheckNullOrUndefined(data.destination, false) && 
            data.destination == WOSChangeEventDestination.WOLanding) {
              this.makeRequest$.next();
          }
        }
    );
    this.statesService.states.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.States = res;
        }
    });
    this.statesService.subStates.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.SubStates = res;
        }
    });
  }

  private setPageMetaFilter() {
    (this.filterForm.get('searchFilters') as FormArray).push(new FormGroup({
      field: new FormControl("PromisedDate", Validators.required),
      operator: new FormControl("today"),
      value: new FormControl(null, Validators.required),
      dateValue: new FormControl(new Date()),
      value1: new FormControl(new Date()),
      data: new FormControl(null),
      isdataloading: new FormControl(null),
      value2: new FormControl(null)
    }));
    this.toggleFilterOptions();
    this.changeDetector.detectChanges();
    this.filterOptionsVc.ApplyFilters();
  }

  private setDefaultFilter() {
    const cancelId = this.commonMethod.CompanyModel.CodeTypeData.WorkTicketStatus.find(x => x.Code.toLowerCase() == "cancelled").ID;
    const voidId = this.commonMethod.CompanyModel.CodeTypeData.WorkTicketStatus.find(x => x.Code.toLowerCase() == "void").ID;

    if (!this.state.filter) {
      this.state.filter = { logic: "and", filters: [] };
    }

    const cancelledFilter = (<any>this.state.filter.filters).filter(x => x.value.indexOf(cancelId) != -1);
    const voidFilter = (<any>this.state.filter.filters).filter(x => x.value.indexOf(voidId) != -1);

    // Apply default filters only when not applied
    // Appened to exisitng filter rather creating a new one
    const filter = (<any>this.state.filter.filters).find(x => x.field == "StatusID" && x.operator == "nin");
    if (filter == undefined) {
      let nIndata = [];
      if (cancelledFilter.length == 0) nIndata.push(cancelId);
      if (voidFilter.length == 0) nIndata.push(voidId);
      if (nIndata.length > 0) {
        this.state.filter.filters.push({
          field: "StatusID",
          operator: "nin",
          value: nIndata
        });
      }
    } else {
      if (voidFilter.length == 0)
        filter.value.push(voidId);
      if (cancelledFilter.length == 0)
        filter.value.push(cancelId);
    }

    const statusFilter = (<any>this.state.filter.filters).find(x => x.field == "StatusID");
    if (statusFilter == undefined) {
      const statusVal = [];
      this.commonMethod.CompanyModel.CodeTypeData.WorkTicketStatus.forEach(
        e => {
          if (e.Code.toLowerCase() != "void") statusVal.push(e.ID);
        }
      );
      this.state.filter.filters.push({
        field: "StatusID",
        operator: "in",
        value: statusVal
      });
    }
  }
  
  private showDispatchOrderOptions(dataItem) {
    if (!CommonMethods.CheckNullOrUndefined(dataItem.Technician, false)) {
      if (!CommonMethods.CheckNullOrUndefined(dataItem.Status, false)) {
          if (dataItem.Status.Code.toLowerCase() == "open" &&
            dataItem.SubStatus.Code.toLowerCase() == "assigned") {
            return true;
          } 
          else { return false; }
      } else return false;
    }
  }
  
  private updateWorkOrderStatus(dataItem) {
    this.spinnerServ.showloader(this.gridVc, this.tabCounter, 0);
    let data = {
      ID: dataItem.ID,
      CancellationComment: "",
      WorkTicketStatusAction: "",
      CancellationReason: null,
      VoidComment: "",
      VoidReason: null,
      JobSite: { ID: dataItem.JobSite.ID },
      Customer: { ID: dataItem.CustomerID },
      Status: {},
      SubStatus: {}
    };
    let StatusID = "";
    let SubStatusID = "";

    StatusID = this.States.find(x => x.Code.toLowerCase() == "open").ID;
    SubStatusID = this.SubStates.find(x => x.Code.toLowerCase() == "dispatched").ID;
    data.WorkTicketStatusAction = "Dispatch";
    data.Status = { ID: StatusID };
    data.SubStatus = { ID: SubStatusID };

    this.httpService
      .ChangeStatus(data)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        response => {
          this.spinnerServ.hideLoader(this.gridVc, this.tabCounter);
          let primaryTech = this.primaryTechnician.transform(
            response.UserAppointments
          )["TechnicianName"];
          this.toastr.success(
            Constants.DefaultWorkOrderMessages.WorkOrderDispatchedWithWOMessage(
              dataItem.WorkTicketNo,
              primaryTech
            ),
            "Success!"
          );
          this.dataSharing.WOSChangeEvent({ destination: WOSChangeEventDestination.WOLanding, response: response });
        },
        error => {
            this.spinnerServ.hideLoader(this.gridVc, this.tabCounter);
          if (!CommonMethods.CheckNullOrUndefined(error.error, true))
            this.toastr.showMultiLineError(error.error.Message, "Error");
        }
      );
  }
    
  private async mapExportData(r) {
    this.isExport = false;
    if (this.exportType == ExportType.Excel) {
      this.exportServ.ExportToExcel({
        exportData: r.Data.map(x => MapExportData.mapWorOrder(x)),
        exportHeadersColumns: ExportColumnHeaders.WorkOrderColumns,
        columnHeaderNotToBeIncluded: []
      });
    } else if (this.exportType == ExportType.PDF) {
      this.exportServ.ExportToPDF({
        exportData: r.Data.map(x => MapExportData.mapWorOrder(x)),
        exportHeadersColumns: ExportColumnHeaders.WorkOrderColumns,
        columnHeaderNotToBeIncluded: []
      });
    } else if (this.exportType == ExportType.CSV) {
      this.exportServ.ExportToCSV({
        exportData: r.Data.map(x => MapExportData.mapWorOrder(x)),
        exportHeadersColumns: ExportColumnHeaders.WorkOrderColumns,
        columnHeaderNotToBeIncluded: []
      });
    }
  }
}
